/**
 * Umami Analytics Integration
 *
 * Provides page view tracking (automatic) and custom event tracking.
 * Gracefully degrades when Umami is not loaded (e.g., local development).
 */

declare global {
  interface Window {
    umami?: {
      track: (
        eventName: string,
        eventData?: Record<string, string | number>,
      ) => void;
    };
  }
}

/**
 * Initialize Umami analytics by injecting the tracking script.
 * Call once at app startup (in main.tsx).
 *
 * Does nothing if VITE_UMAMI_WEBSITE_ID is not set.
 */
export function initAnalytics(): void {
  const websiteId = import.meta.env.VITE_UMAMI_WEBSITE_ID;

  if (!websiteId) {
    if (import.meta.env.DEV) {
      console.debug("[Analytics] Umami disabled - no website ID configured");
    }
    return;
  }

  // Avoid double-initialization
  if (document.querySelector("script[data-website-id]")) {
    return;
  }

  const script = document.createElement("script");
  script.defer = true;
  script.src = "https://cloud.umami.is/script.js";
  script.setAttribute("data-website-id", websiteId);
  document.head.appendChild(script);

  if (import.meta.env.DEV) {
    console.debug("[Analytics] Umami initialized with website ID:", websiteId);
  }
}

/**
 * Track a custom event in Umami.
 *
 * No-op if Umami is not loaded or website ID is not configured.
 * Safe to call anywhere - will not throw if Umami is unavailable.
 *
 * @param eventName - Snake_case event name (e.g., "signup_completed")
 * @param eventData - Optional key-value data (no PII - no emails, names, IDs)
 *
 * @example
 * trackEvent("tier_selected", { tier: "pro" });
 * trackEvent("site_created");
 */
export function trackEvent(
  eventName: string,
  eventData?: Record<string, string | number>,
): void {
  if (typeof window !== "undefined" && window.umami) {
    window.umami.track(eventName, eventData);
  }
}

/**
 * Standard event names for consistency across the app.
 * Use these constants instead of string literals.
 */
export const AnalyticsEvents = {
  // Auth flow
  SIGNUP_STARTED: "signup_started",
  SIGNUP_COMPLETED: "signup_completed",

  // Onboarding flow
  ONBOARDING_STEP: "onboarding_step",

  // Site management
  SITE_CREATED: "site_created",

  // Billing flow
  TIER_SELECTED: "tier_selected",
  CHECKOUT_STARTED: "checkout_started",
  CHECKOUT_COMPLETED: "checkout_completed",
  ENTERPRISE_CONTACT: "enterprise_contact",
} as const;
